import Phaser from "phaser";
import { goTo } from "../ui/sceneTransition";
import { GAME_WIDTH, GAME_HEIGHT } from "../config";
import { THEME, panel, button } from "../ui/theme";
import { addQuitButton, beginSceneLifecycle, addMenuBackdrop, addMenuHeader, addContentPanel, addCastleBackdrop, type SceneLifecycle } from "../ui/menuUi";
import { getSession, getUserId, signOut } from "../backend/auth";
import { fetchMyProfile, updateMyDisplayName, deleteMyAccount, updateMyAvatar, MyProfile } from "../backend/profile";
import { fetchCareerStats, fetchRecentRuns, fetchPublicProfile, CareerStats, RunRow, PublicProfile } from "../backend/profileStats";
import { save, xpForLevel, MAX_PLAYER_LEVEL } from "../systems/Save";
import { CAMPAIGN_MAPS } from "../maps/maps";
import { isBackendConfigured } from "../backend/client";
import { sendFriendRequest, blockUser } from "../backend/friends";
import { showAlert, showConfirm, showPrompt } from "../ui/modal";
import { drawAvatar, COA_PORTRAIT_START, COA_COUNT, parseAvatarId } from "../ui/avatar";
import {
  generateCrestFromId,
  CREST_CHARGE_NAMES,
  ALL_CREST_COLORS,
  ALL_CREST_COLOR_NAMES
} from "../assets/generateTextures";

interface TitleDef {
  key: string;
  label: string;
  levelReq: number;
}

const TITLES: TitleDef[] = [
  { key: "", label: "None", levelReq: 0 },
  { key: "Squire", label: "Squire", levelReq: 5 },
  { key: "Knight", label: "Knight", levelReq: 15 },
  { key: "Champion", label: "Champion", levelReq: 25 },
  { key: "Paladin", label: "Paladin", levelReq: 40 },
  { key: "Baron", label: "Baron", levelReq: 55 },
  { key: "Baroness", label: "Baroness", levelReq: 55 },
  { key: "Count", label: "Count", levelReq: 70 },
  { key: "Countess", label: "Countess", levelReq: 70 },
  { key: "Duke", label: "Duke", levelReq: 85 },
  { key: "Duchess", label: "Duchess", levelReq: 85 },
  { key: "Archmage", label: "Archmage", levelReq: 100 },
  { key: "Lord", label: "Lord", levelReq: 110 },
  { key: "Lady", label: "Lady", levelReq: 110 },
  { key: "King", label: "King", levelReq: 120 },
  { key: "Queen", label: "Queen", levelReq: 120 },
];

const DIFF_LABELS: Record<number, string> = { 1: "Squire", 2: "Knight", 3: "Lord" };

// Scrollable viewport inside the content panel (panel spans y 96..700).
const VIEW_TOP = 112;
const VIEW_H = 576;

function fmtDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function fmtDate(iso: string): string {
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
}

function mapName(key: string): string {
  return CAMPAIGN_MAPS.find(m => m.key === key)?.name ?? key;
}

export class ProfileScene extends Phaser.Scene {
  private readOnly = false;
  private targetUserId: string | null = null;
  private lifecycle!: SceneLifecycle;

  // Scrolling
  private scrollY = 0;
  private maxScroll = 0;
  private content!: Phaser.GameObjects.Container;
  private scrollBar?: Phaser.GameObjects.Rectangle;
  private scrollBarTrack?: Phaser.GameObjects.Rectangle;
  private wheelHandler?: (
    ptr: Phaser.Input.Pointer,
    over: Phaser.GameObjects.GameObject[],
    dx: number, dy: number
  ) => void;

  // Identity header refs for in-place refresh after avatar changes
  private identityAvatar?: Phaser.GameObjects.Image;
  private identityTitleText?: Phaser.GameObjects.Text;
  private identityNameText?: Phaser.GameObjects.Text;
  private identityY = VIEW_TOP;

  // Debounced backend write for instant-save customization
  private avatarSaveTimer: number | null = null;
  private pendingAvatarId: string | null = null;

  constructor() { super("ProfileScene"); }

  create(): void {
    this.lifecycle = beginSceneLifecycle(this);
    this.scrollY = 0;
    this.maxScroll = 0;
    this.scrollBar = undefined;
    this.scrollBarTrack = undefined;
    this.identityAvatar = undefined;
    this.identityTitleText = undefined;
    this.identityNameText = undefined;
    this.avatarSaveTimer = null;
    this.pendingAvatarId = null;

    const viewId = this.registry.get("viewProfileUserId") as string | undefined;
    if (viewId) {
      this.readOnly = true;
      this.targetUserId = viewId;
      this.registry.remove("viewProfileUserId");
    } else {
      this.readOnly = false;
      this.targetUserId = getUserId();
    }

    // Backdrop & Header
    addMenuBackdrop(this, { darkness: 0.72 });
    addContentPanel(this, { x: 24, y: 96, width: GAME_WIDTH - 48, height: GAME_HEIGHT - 116 }, "iron", 0.96);
    addMenuHeader(this, {
      title: "PROFILE",
      onBack: () => goTo(this, "MenuScene")
    });
    addQuitButton(this);

    this.lifecycle.key("keydown-ESC", () => goTo(this, "MenuScene"));
    this.lifecycle.key("keydown-UP", () => this.applyScroll(-48));
    this.lifecycle.key("keydown-DOWN", () => this.applyScroll(48));
    this.lifecycle.key("keydown-PAGEUP", () => this.applyScroll(-VIEW_H * 0.8));
    this.lifecycle.key("keydown-PAGEDOWN", () => this.applyScroll(VIEW_H * 0.8));

    // Flush a pending avatar write when leaving the scene.
    this.lifecycle.add(() => {
      if (this.avatarSaveTimer !== null) {
        window.clearTimeout(this.avatarSaveTimer);
        this.avatarSaveTimer = null;
      }
      if (this.pendingAvatarId) {
        void updateMyAvatar(this.pendingAvatarId);
        this.pendingAvatarId = null;
      }
      if (this.wheelHandler) {
        this.input.off("wheel", this.wheelHandler);
        this.wheelHandler = undefined;
      }
    });

    if (!isBackendConfigured() || (!this.readOnly && !getSession())) {
      this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, "Backend not configured or not signed in.", {
        fontFamily: THEME.fonts.body, fontSize: "18px", color: THEME.colors.parchmentDim
      }).setOrigin(0.5);
      return;
    }

    void this.loadData();
  }

  private async loadData(): Promise<void> {
    const uid = this.targetUserId;
    if (!uid) return;

    const statusText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, "Loading...", {
      fontFamily: THEME.fonts.body, fontSize: "18px", color: THEME.colors.parchmentDim
    }).setOrigin(0.5);

    let profile: PublicProfile | null = null;
    let myProfile: MyProfile | null = null;
    let stats: CareerStats;
    let runs: RunRow[];

    if (this.readOnly) {
      [profile, stats, runs] = await Promise.all([
        fetchPublicProfile(uid),
        fetchCareerStats(uid),
        fetchRecentRuns(uid, 10)
      ]);
    } else {
      [myProfile, stats, runs] = await Promise.all([
        fetchMyProfile(),
        fetchCareerStats(uid),
        fetchRecentRuns(uid, 10)
      ]);
    }

    if (!this.scene.isActive()) return;
    statusText.destroy();

    const displayName = this.readOnly
      ? (profile?.displayName ?? "Unknown")
      : (myProfile?.displayName ?? "Anonymous");
    const friendCode = this.readOnly
      ? (profile?.friendCode ?? "")
      : (myProfile?.friendCode ?? "");
    const createdAt = this.readOnly
      ? (profile?.createdAt ?? "")
      : (myProfile?.createdAt ?? "");
    const avatarId = this.readOnly
      ? (profile?.avatarId ?? null)
      : (myProfile?.avatarId ?? save.load().avatarId ?? null);

    this.renderProfile(uid, displayName, friendCode, createdAt, avatarId, stats, runs);
  }

  /** True when the pointer is inside the scroll viewport. Masked-out objects
   *  still receive input in Phaser, so every in-container click checks this. */
  private inView(p: Phaser.Input.Pointer): boolean {
    return p.y >= VIEW_TOP && p.y <= VIEW_TOP + VIEW_H;
  }

  private renderProfile(
    uid: string,
    displayName: string,
    friendCode: string,
    createdAt: string,
    avatarId: string | null,
    stats: CareerStats,
    runs: RunRow[]
  ): void {
    this.content = this.add.container(0, 0);
    const maskShape = this.add.graphics().setVisible(false);
    maskShape.fillRect(28, VIEW_TOP, GAME_WIDTH - 56, VIEW_H);
    this.content.setMask(maskShape.createGeometryMask());

    let y = VIEW_TOP;
    if (this.readOnly) {
      y += this.buildIdentity(y, uid, displayName, friendCode, createdAt, avatarId) + 12;
    } else {
      y += this.buildOwnCard(y, uid, displayName, friendCode, createdAt, avatarId) + 12;
    }
    y += this.buildStatsRow(y, stats) + 12;
    y += this.buildRecentRuns(y, runs) + 24;
    y += this.buildActionButtons(y) + 24;

    this.addScrollBackdrop(y);
    this.maxScroll = Math.max(0, y - (VIEW_TOP + VIEW_H));
    this.rebuildScrollBar();
    this.wheelHandler = (_p, _o, _dx, dy) => this.applyScroll(dy * 0.6);
    this.input.on("wheel", this.wheelHandler);

    // delete account stays fixed outside the scroll flow
    if (!this.readOnly) {
      const danger = this.add.text(GAME_WIDTH - 24, GAME_HEIGHT - 12, "delete account", {
        fontFamily: THEME.fonts.body, fontSize: "11px", color: THEME.colors.lock
      }).setOrigin(1, 1).setInteractive({ useHandCursor: true });
      danger.on("pointerover", () => danger.setColor(THEME.colors.blood));
      danger.on("pointerout",  () => danger.setColor(THEME.colors.lock));
      danger.on("pointerdown", () => {
        void showConfirm(this, "Delete Account",
          "Are you sure you want to permanently delete your account? This cannot be undone."
        ).then(async ok => {
          if (!ok) return;
          const res = await deleteMyAccount();
          if (!this.scene.isActive()) return;
          if (res.error) { await showAlert(this, "Error", res.error); return; }
          await showAlert(this, "Deleted", "Account deleted.");
          goTo(this, "MenuScene");
        });
      });
    }
  }

  /** Castle art + dark veil spanning the full scrollable content, inserted
   *  below every section so it scrolls along with the page. */
  private addScrollBackdrop(totalH: number): void {
    const x = 24, top = 96;
    const w = GAME_WIDTH - 48;
    const h = Math.max(totalH - top, GAME_HEIGHT - 116);

    addCastleBackdrop(this, { x, y: top, width: w, height: h }, {
      veil: "dark",
      mask: false,
      parent: this.content,
      insertAt: 0,
    });
  }

  // ── Identity card (read-only profiles) ─────────────────────────────────────
  private buildIdentity(
    y: number, uid: string, displayName: string,
    friendCode: string, createdAt: string, avatarId: string | null
  ): number {
    const H = 180;
    this.identityY = y;
    this.content.add(panel(this, 40, y, 1200, H, "iron"));

    this.identityAvatar = drawAvatar(this, 130, y + 90, 140, avatarId, uid);
    this.content.add(this.identityAvatar);

    const { title } = parseAvatarId(avatarId);

    this.identityTitleText = this.add.text(220, y + 2, title ? title.toUpperCase() : "", {
      fontFamily: THEME.fonts.display, fontSize: "12px", color: THEME.colors.gold
    });
    this.content.add(this.identityTitleText);

    const nameY = y + (title ? 22 : 20);
    this.identityNameText = this.add.text(220, nameY, displayName, {
      fontFamily: THEME.fonts.display, fontSize: "26px", color: THEME.colors.parchment
    });
    this.content.add(this.identityNameText);

    // Friend Code
    if (friendCode) {
      const fcText = this.add.text(220, y + 60, `Friend Code: ${friendCode}`, {
        fontFamily: THEME.fonts.body, fontSize: "15px", color: THEME.colors.parchmentDim
      });
      this.content.add(fcText);
      const copyBtn = this.add.text(220 + fcText.width + 12, y + 60, "[Copy]", {
        fontFamily: THEME.fonts.body, fontSize: "14px", color: THEME.colors.goldDim
      }).setInteractive({ useHandCursor: true });
      copyBtn.on("pointerover", () => copyBtn.setColor(THEME.colors.gold));
      copyBtn.on("pointerout", () => copyBtn.setColor(THEME.colors.goldDim));
      copyBtn.on("pointerdown", (p: Phaser.Input.Pointer) => {
        if (!this.inView(p)) return;
        void navigator.clipboard.writeText(friendCode);
        void showAlert(this, "Copied", `Friend code "${friendCode}" copied.`);
      });
      this.content.add(copyBtn);
    }

    // Member since
    if (createdAt) {
      this.content.add(this.add.text(220, y + 90, `Member since ${fmtDate(createdAt)}`, {
        fontFamily: THEME.fonts.body, fontSize: "14px", color: THEME.colors.parchmentDim
      }));
    }

    return H;
  }

  // ── Own profile: identity info + inline customization, one avatar ──────────
  private buildOwnCard(
    y: number, uid: string, displayName: string,
    friendCode: string, createdAt: string, avatarId: string | null
  ): number {
    const { id: cleanAvatarId, title: currentTitle } = parseAvatarId(avatarId);
    const currentId = cleanAvatarId || "gules-lion-or";
    const isPremium = currentId.startsWith("coa-");

    let usePortrait = isPremium;
    let selectedCoaIndex = isPremium ? parseInt(currentId.slice(4), 10) : 15;
    if (isNaN(selectedCoaIndex) || selectedCoaIndex < COA_PORTRAIT_START) {
      selectedCoaIndex = 15;
    }
    let [field, charge, metal] = !isPremium && currentId.includes("-")
      ? currentId.split("-")
      : ["gules", "lion", "or"];
    let selectedTitle = currentTitle || "";

    // Two-column layout: crest/portrait customization on the left, TITLE on
    // the right (previously stacked full-width below everything, leaving the
    // right side of the panel empty while the left side scrolled forever).
    const colX = 300;
    const colX2 = 780;
    const stencilCols = 5;
    const stencilColGap = 86;
    const stencilRows = Math.ceil(CREST_CHARGE_NAMES.length / stencilCols);
    const portraitCount = COA_COUNT - COA_PORTRAIT_START + 1;
    const portraitRows = Math.ceil(portraitCount / 8);
    const titleCols = 2;
    const titleColGap = 220;
    const titleRows = Math.ceil(TITLES.length / titleCols);

    const fieldLabelY = y + 150;
    const fieldRowY = fieldLabelY + 26;
    const metalLabelY = fieldRowY + 26;
    const metalRowY = metalLabelY + 26;
    const stencilLabelY = metalRowY + 26;
    const stencilTopY = stencilLabelY + 26;
    const portraitLabelY = stencilTopY + stencilRows * 26 + 14;
    const portraitTopY = portraitLabelY + 44;
    const leftColumnBottom = portraitTopY + (portraitRows - 1) * 46 + 40;

    const titleLabelY = fieldLabelY;
    const titleTopY = titleLabelY + 32;
    const titleColumnBottom = titleTopY + (titleRows - 1) * 30 + 30;

    const H = Math.max(leftColumnBottom, titleColumnBottom) + 20 - y;

    this.content.add(panel(this, 40, y, 1200, H, "iron"));

    // The one and only avatar: live preview on the left, name + title below.
    const previewImage = this.add.image(160, y + 180, "px-white");
    this.content.add(previewImage);
    const previewTitleText = this.add.text(160, y + 272, "", {
      fontFamily: THEME.fonts.display, fontSize: "11px", color: THEME.colors.gold
    }).setOrigin(0.5);
    this.content.add(previewTitleText);
    const previewNameText = this.add.text(160, y + 292, "", {
      fontFamily: THEME.fonts.display, fontSize: "20px", color: THEME.colors.parchment
    }).setOrigin(0.5);
    this.content.add(previewNameText);

    // Identity info block, top-right next to the avatar.
    const data = save.load();
    const levelChipW = 60, levelChipH = 22;
    this.content.add(panel(this, colX, y + 20, levelChipW, levelChipH, "iron"));
    this.content.add(this.add.text(colX + levelChipW / 2, y + 20 + levelChipH / 2, `LV ${data.playerLevel}`, {
      fontFamily: THEME.fonts.display, fontSize: "13px", color: THEME.colors.gold
    }).setOrigin(0.5));

    const editBtn = this.add.text(colX + levelChipW + 12, y + 23, "[edit name]", {
      fontFamily: THEME.fonts.body, fontSize: "14px", color: THEME.colors.goldDim
    }).setInteractive({ useHandCursor: true });
    editBtn.on("pointerover", () => editBtn.setColor(THEME.colors.gold));
    editBtn.on("pointerout", () => editBtn.setColor(THEME.colors.goldDim));
    editBtn.on("pointerdown", (p: Phaser.Input.Pointer) => {
      if (!this.inView(p)) return;
      void showPrompt(this, "Edit Display Name", "Enter new display name (2-24 chars):", displayName)
        .then(async newName => {
          if (!newName || newName === displayName) return;
          const res = await updateMyDisplayName(newName);
          if (!this.scene.isActive()) return;
          if (res.error) { await showAlert(this, "Error", res.error); }
          else { this.scene.restart(); }
        });
    });
    this.content.add(editBtn);

    if (friendCode) {
      const fcText = this.add.text(colX, y + 56, `Friend Code: ${friendCode}`, {
        fontFamily: THEME.fonts.body, fontSize: "15px", color: THEME.colors.parchmentDim
      });
      this.content.add(fcText);
      const copyBtn = this.add.text(colX + fcText.width + 12, y + 56, "[Copy]", {
        fontFamily: THEME.fonts.body, fontSize: "14px", color: THEME.colors.goldDim
      }).setInteractive({ useHandCursor: true });
      copyBtn.on("pointerover", () => copyBtn.setColor(THEME.colors.gold));
      copyBtn.on("pointerout", () => copyBtn.setColor(THEME.colors.goldDim));
      copyBtn.on("pointerdown", (p: Phaser.Input.Pointer) => {
        if (!this.inView(p)) return;
        void navigator.clipboard.writeText(friendCode);
        void showAlert(this, "Copied", `Friend code "${friendCode}" copied.`);
      });
      this.content.add(copyBtn);
    }

    if (createdAt) {
      this.content.add(this.add.text(colX, y + 84, `Member since ${fmtDate(createdAt)}`, {
        fontFamily: THEME.fonts.body, fontSize: "14px", color: THEME.colors.parchmentDim
      }));
    }

    {
      const atCap = data.playerLevel >= MAX_PLAYER_LEVEL;
      const need = atCap ? 0 : xpForLevel(data.playerLevel);
      const pct = atCap ? 1 : Math.min(1, data.playerXp / need);
      const barW = 320, barH = 14;
      this.content.add(this.add.rectangle(colX, y + 112, barW, barH, 0x2a1d10, 0.8).setOrigin(0).setStrokeStyle(1, THEME.hex.goldDim));
      this.content.add(this.add.rectangle(colX + 1, y + 113, Math.max(2, (barW - 2) * pct), barH - 2, THEME.hex.gold).setOrigin(0));
      this.content.add(this.add.text(colX + barW / 2, y + 112 + barH / 2,
        atCap ? "MAX LEVEL" : `${data.playerXp} / ${need} XP`, {
          fontFamily: THEME.fonts.body, fontSize: "11px", color: THEME.colors.ironDeep
        }).setOrigin(0.5));
    }

    const updatePreview = () => {
      if (this.textures.exists("avatar-preview")) {
        this.textures.remove("avatar-preview");
      }
      if (usePortrait) {
        const coaKey = `coa-${selectedCoaIndex}`;
        previewImage.setTexture(coaKey);
        const tex = this.textures.get(coaKey);
        const frame = tex ? tex.get() : null;
        const texWidth = frame ? frame.width : 192;
        previewImage.setScale(160 / texWidth);
      } else {
        const previewId = `${field}-${charge}-${metal}`;
        generateCrestFromId(this, previewId, "avatar-preview");
        previewImage.setTexture("avatar-preview");
        previewImage.setScale(160 / 192);
      }
      const rawName = displayName || "Anonymous";
      previewNameText.setText(rawName.toUpperCase());
      if (selectedTitle) {
        previewTitleText.setText(selectedTitle.toUpperCase());
        previewTitleText.setVisible(true);
        previewNameText.setY(y + 292);
      } else {
        previewTitleText.setVisible(false);
        previewNameText.setY(y + 282);
      }
    };

    // Instant save: apply locally right away, debounce the backend write.
    const applyAvatar = () => {
      const avatarPart = usePortrait ? `coa-${selectedCoaIndex}` : `${field}-${charge}-${metal}`;
      const finalId = selectedTitle ? `${avatarPart}:${selectedTitle}` : avatarPart;

      const sd = save.load();
      sd.avatarId = finalId;
      save.markDirty();
      if (this.textures.exists(`avatar-${uid}`)) this.textures.remove(`avatar-${uid}`);
      if (this.textures.exists(`crest-${uid}`)) this.textures.remove(`crest-${uid}`);

      this.pendingAvatarId = finalId;
      if (this.avatarSaveTimer !== null) window.clearTimeout(this.avatarSaveTimer);
      this.avatarSaveTimer = window.setTimeout(() => {
        this.avatarSaveTimer = null;
        const id = this.pendingAvatarId;
        this.pendingAvatarId = null;
        if (!id) return;
        void updateMyAvatar(id).then(async res => {
          if (!this.scene.isActive()) return;
          if (res.error) await showAlert(this, "Error", res.error);
        });
      }, 500);
    };

    // CREST — background color
    this.content.add(this.add.text(colX, fieldLabelY, "BACKGROUND COLOR", {
      fontFamily: THEME.fonts.display, fontSize: "11px", color: THEME.colors.goldDim
    }));
    const fieldButtons: Phaser.GameObjects.Rectangle[] = [];
    ALL_CREST_COLOR_NAMES.forEach((name, idx) => {
      const bg = this.add.rectangle(colX + idx * 34 + 13, fieldRowY + 13, 26, 26, ALL_CREST_COLORS[name])
        .setInteractive({ useHandCursor: true });
      bg.on("pointerdown", (p: Phaser.Input.Pointer) => {
        if (!this.inView(p)) return;
        field = name;
        usePortrait = false;
        updatePreview();
        updateCrestHighlights();
        updatePortraitHighlights();
        applyAvatar();
      });
      fieldButtons.push(bg);
      this.content.add(bg);
    });

    // CREST — emblem color
    this.content.add(this.add.text(colX, metalLabelY, "EMBLEM COLOR", {
      fontFamily: THEME.fonts.display, fontSize: "11px", color: THEME.colors.goldDim
    }));
    const metalButtons: Phaser.GameObjects.Rectangle[] = [];
    ALL_CREST_COLOR_NAMES.forEach((name, idx) => {
      const bg = this.add.rectangle(colX + idx * 34 + 13, metalRowY + 13, 26, 26, ALL_CREST_COLORS[name])
        .setInteractive({ useHandCursor: true });
      bg.on("pointerdown", (p: Phaser.Input.Pointer) => {
        if (!this.inView(p)) return;
        metal = name;
        usePortrait = false;
        updatePreview();
        updateCrestHighlights();
        updatePortraitHighlights();
        applyAvatar();
      });
      metalButtons.push(bg);
      this.content.add(bg);
    });

    // CREST — stencil shape
    this.content.add(this.add.text(colX, stencilLabelY, "STENCIL SHAPE", {
      fontFamily: THEME.fonts.display, fontSize: "11px", color: THEME.colors.goldDim
    }));
    const emblemButtons: Phaser.GameObjects.Container[] = [];
    CREST_CHARGE_NAMES.forEach((name, idx) => {
      const col = idx % stencilCols;
      const row = Math.floor(idx / stencilCols);
      const bx = colX + col * stencilColGap + stencilColGap / 2;
      const by = stencilTopY + row * 26;

      const btnBg = this.add.rectangle(0, 0, stencilColGap - 4, 22, 0x2a1d10)
        .setInteractive({ useHandCursor: true });
      const btnTxt = this.add.text(9, 0, name.toUpperCase(), {
        fontFamily: THEME.fonts.body, fontSize: "9px", color: THEME.colors.parchment
      }).setOrigin(0.5);
      const img = this.add.image(-29, 0, "px-white");
      img.setDisplaySize(18, 18);

      const btnContainer = this.add.container(bx, by, [btnBg, img, btnTxt]);
      btnBg.on("pointerdown", (p: Phaser.Input.Pointer) => {
        if (!this.inView(p)) return;
        charge = name;
        usePortrait = false;
        updatePreview();
        updateCrestHighlights();
        updatePortraitHighlights();
        applyAvatar();
      });
      emblemButtons.push(btnContainer);
      this.content.add(btnContainer);
    });

    const updateCrestHighlights = () => {
      fieldButtons.forEach((b, i) => {
        const isSel = ALL_CREST_COLOR_NAMES[i] === field && !usePortrait;
        b.setStrokeStyle(isSel ? 2 : 1, isSel ? 0xffd66b : 0x554433);
      });
      metalButtons.forEach((b, i) => {
        const isSel = ALL_CREST_COLOR_NAMES[i] === metal && !usePortrait;
        b.setStrokeStyle(isSel ? 2 : 1, isSel ? 0xffd66b : 0x554433);
      });
      emblemButtons.forEach((b, i) => {
        const bgRect = b.list[0] as Phaser.GameObjects.Rectangle;
        const img = b.list[1] as Phaser.GameObjects.Image;
        const isSel = CREST_CHARGE_NAMES[i] === charge && !usePortrait;
        bgRect.setStrokeStyle(isSel ? 2 : 1, isSel ? 0xffd66b : 0x665544);

        const chargeName = CREST_CHARGE_NAMES[i];
        const thumbKey = `crest-thumb-${chargeName}-${field}-${metal}`;
        if (!this.textures.exists(thumbKey)) {
          const previewId = `${field}-${chargeName}-${metal}`;
          generateCrestFromId(this, previewId, thumbKey);
        }
        img.setTexture(thumbKey);
        img.setDisplaySize(18, 18);
        img.setPosition(-29, 0);
      });
    };

    // PORTRAIT
    this.content.add(this.add.text(colX, portraitLabelY, "PORTRAIT", {
      fontFamily: THEME.fonts.display, fontSize: "11px", color: THEME.colors.goldDim
    }));
    const portraitButtons: Phaser.GameObjects.Container[] = [];
    for (let i = COA_PORTRAIT_START; i <= COA_COUNT; i++) {
      const coaKey = `coa-${i}`;
      const idx = i - COA_PORTRAIT_START;
      const col = idx % 8;
      const row = Math.floor(idx / 8);
      const cx = colX + col * 52 + 21;
      const cy = portraitTopY + row * 46;

      const bg = this.add.rectangle(0, 0, 44, 40, 0x2a1d10)
        .setInteractive({ useHandCursor: true });
      const img = this.add.image(0, 0, coaKey);
      const tex = this.textures.get(coaKey);
      const frame = tex ? tex.get() : null;
      const texWidth = frame ? frame.width : 192;
      img.setScale(36 / texWidth);

      const coaContainer = this.add.container(cx, cy, [bg, img]);
      bg.on("pointerdown", (p: Phaser.Input.Pointer) => {
        if (!this.inView(p)) return;
        selectedCoaIndex = i;
        usePortrait = true;
        updatePreview();
        updateCrestHighlights();
        updatePortraitHighlights();
        applyAvatar();
      });
      portraitButtons.push(coaContainer);
      this.content.add(coaContainer);
    }

    const updatePortraitHighlights = () => {
      portraitButtons.forEach((c, idx) => {
        const bgRect = c.list[0] as Phaser.GameObjects.Rectangle;
        const i = COA_PORTRAIT_START + idx;
        const isSel = i === selectedCoaIndex && usePortrait;
        bgRect.setStrokeStyle(isSel ? 2 : 1, isSel ? 0xffd66b : 0x665544);
      });
    };

    // TITLE — right column, alongside the crest/portrait customization.
    this.content.add(this.add.text(colX2, titleLabelY, "TITLE", {
      fontFamily: THEME.fonts.display, fontSize: "11px", color: THEME.colors.goldDim
    }));
    const titleButtons: Phaser.GameObjects.Container[] = [];
    const playerLevel = save.load().playerLevel;

    TITLES.forEach((t, idx) => {
      const col = idx % titleCols;
      const row = Math.floor(idx / titleCols);
      const bx = colX2 + col * titleColGap + titleColGap / 2 - 6;
      const by = titleTopY + row * 30;

      const isLocked = playerLevel < t.levelReq;
      const btnBg = this.add.rectangle(0, 0, titleColGap - 16, 22, isLocked ? 0x1a120c : 0x2a1d10)
        .setStrokeStyle(1, 0x665544)
        .setInteractive({ useHandCursor: !isLocked });

      const labelText = t.label ? t.label.toUpperCase() : "NONE";
      const displayStr = isLocked ? `${labelText} (LVL ${t.levelReq})` : labelText;
      const btnTxt = this.add.text(0, 0, displayStr, {
        fontFamily: THEME.fonts.body,
        fontSize: "10px",
        color: isLocked ? THEME.colors.lock : THEME.colors.parchment
      }).setOrigin(0.5);

      const titleContainer = this.add.container(bx, by, [btnBg, btnTxt]);
      if (!isLocked) {
        btnBg.on("pointerdown", (p: Phaser.Input.Pointer) => {
          if (!this.inView(p)) return;
          selectedTitle = t.key;
          updatePreview();
          updateTitleHighlights();
          applyAvatar();
        });
        btnBg.on("pointerover", () => btnBg.setStrokeStyle(1.5, 0xffd66b));
        btnBg.on("pointerout", () => {
          const isSel = selectedTitle === t.key;
          btnBg.setStrokeStyle(isSel ? 2 : 1, isSel ? 0xffd66b : 0x665544);
        });
      }
      titleButtons.push(titleContainer);
      this.content.add(titleContainer);
    });

    const updateTitleHighlights = () => {
      TITLES.forEach((t, idx) => {
        const bgRect = titleButtons[idx].list[0] as Phaser.GameObjects.Rectangle;
        const isSel = selectedTitle === t.key;
        const isLocked = playerLevel < t.levelReq;
        if (!isLocked) {
          bgRect.setStrokeStyle(isSel ? 2 : 1, isSel ? 0xffd66b : 0x665544);
        }
      });
    };

    updateCrestHighlights();
    updatePortraitHighlights();
    updateTitleHighlights();
    updatePreview();

    return H;
  }

  // ── Career stats + campaign progress ───────────────────────────────────────
  private buildStatsRow(y: number, stats: CareerStats): number {
    const H = 180;
    const data = save.load();

    this.content.add(panel(this, 40, y, 590, H, "iron"));
    this.content.add(this.add.text(60, y + 20, "CAREER STATS", {
      fontFamily: THEME.fonts.display, fontSize: "18px", color: THEME.colors.gold
    }));

    const winRate = stats.runsPlayed > 0 ? Math.round((stats.runsWon / stats.runsPlayed) * 100) : 0;
    const bestMapName = stats.bestMapKey ? mapName(stats.bestMapKey) : "—";
    const bestDiffLabel = stats.bestDifficulty ? (DIFF_LABELS[stats.bestDifficulty] ?? "?") : "";
    const bestScoreStr = stats.bestScore > 0 ? `${stats.bestScore} (${bestMapName} / ${bestDiffLabel})` : "—";
    const fastestStr = stats.fastestWinMs !== null ? fmtDuration(stats.fastestWinMs) : "—";

    const drawRow = (label: string, value: string, rowY: number) => {
      this.content.add(this.add.text(60, rowY, label, {
        fontFamily: THEME.fonts.body, fontSize: "14px", color: THEME.colors.parchmentDim
      }));
      this.content.add(this.add.text(570, rowY, value, {
        fontFamily: THEME.fonts.body, fontSize: "14px", color: THEME.colors.parchment
      }).setOrigin(1, 0));
    };

    drawRow("Runs Played", String(stats.runsPlayed), y + 52);
    drawRow("Runs Won", String(stats.runsWon), y + 74);
    drawRow("Win Rate", `${winRate}%`, y + 96);
    drawRow("Best Score", bestScoreStr, y + 118);
    drawRow("Fastest Win", fastestStr, y + 140);

    this.content.add(panel(this, 650, y, 590, H, "iron"));
    this.content.add(this.add.text(670, y + 20, "CAMPAIGN PROGRESS", {
      fontFamily: THEME.fonts.display, fontSize: "18px", color: THEME.colors.gold
    }));

    if (this.readOnly) {
      this.content.add(this.add.text(650 + 295, y + 90, "Campaign progress is private.", {
        fontFamily: THEME.fonts.body, fontSize: "14px", color: THEME.colors.parchmentDim
      }).setOrigin(0.5));
    } else {
      for (const [index, m] of CAMPAIGN_MAPS.entries()) {
        const cleared = data.campaign.cleared[m.key] ?? 0;
        const stars = cleared === 0 ? "☆☆☆"
          : cleared === 1 ? "★☆☆"
          : cleared === 2 ? "★★☆" : "★★★";

        const col = index % 3;
        const row = Math.floor(index / 3);
        const cellX = 670 + col * 185;
        const cellY = y + 52 + row * 40;

        this.content.add(this.add.text(cellX, cellY, m.name.length > 18 ? `${m.name.slice(0, 16)}…` : m.name, {
          fontFamily: THEME.fonts.body, fontSize: "12px", color: THEME.colors.parchmentDim
        }));
        this.content.add(this.add.text(cellX, cellY + 16, stars, {
          fontFamily: THEME.fonts.display, fontSize: "11px", color: THEME.colors.gold
        }));
      }
    }

    return H;
  }

  // ── Recent runs (flat rows — the page itself scrolls) ─────────────────────
  private buildRecentRuns(y: number, runs: RunRow[]): number {
    const rowH = 22;
    const rowsTop = y + 62;
    const H = runs.length === 0 ? 110 : (rowsTop - y) + runs.length * rowH + 16;

    this.content.add(panel(this, 40, y, 1200, H, "iron"));
    this.content.add(this.add.text(60, y + 16, "RECENT RUNS", {
      fontFamily: THEME.fonts.display, fontSize: "18px", color: THEME.colors.gold
    }));

    const headers = [
      ["MAP", 60], ["DIFFICULTY", 300], ["OUTCOME", 500],
      ["SCORE", 700], ["DURATION", 880], ["DATE", 1060]
    ] as const;
    headers.forEach(([lbl, px]) => {
      this.content.add(this.add.text(px, y + 42, lbl, {
        fontFamily: THEME.fonts.display, fontSize: "12px", color: THEME.colors.goldDim
      }));
    });

    if (runs.length === 0) {
      this.content.add(this.add.text(60, rowsTop + 6, "No runs recorded yet.", {
        fontFamily: THEME.fonts.body, fontSize: "14px", color: THEME.colors.parchmentDim
      }));
      return H;
    }

    let rowY = rowsTop;
    for (const run of runs) {
      const mn = mapName(run.mapKey);
      const diff = DIFF_LABELS[run.difficulty] ?? "?";
      const outcome = run.outcome === "win" ? "WIN" : "LOSS";
      const color = run.outcome === "win" ? THEME.colors.gold : THEME.colors.blood;

      this.content.add(this.add.text(60, rowY, mn, { fontFamily: THEME.fonts.body, fontSize: "13px", color: THEME.colors.parchment }));
      this.content.add(this.add.text(300, rowY, diff, { fontFamily: THEME.fonts.body, fontSize: "13px", color: THEME.colors.parchmentDim }));
      this.content.add(this.add.text(500, rowY, outcome, { fontFamily: THEME.fonts.body, fontSize: "13px", color }));
      this.content.add(this.add.text(700, rowY, String(run.score), { fontFamily: THEME.fonts.body, fontSize: "13px", color: THEME.colors.parchment }));
      this.content.add(this.add.text(880, rowY, fmtDuration(run.durationMs), { fontFamily: THEME.fonts.body, fontSize: "13px", color: THEME.colors.parchmentDim }));
      this.content.add(this.add.text(1060, rowY, fmtDate(run.submittedAt), { fontFamily: THEME.fonts.body, fontSize: "13px", color: THEME.colors.parchmentDim }));
      rowY += rowH;
    }

    return H;
  }

  // ── Action buttons ─────────────────────────────────────────────────────────
  private buildActionButtons(y: number): number {
    const btnY = y + 18;

    if (this.readOnly) {
      const addBtn = button(this, GAME_WIDTH / 2 - 100, btnY, 180, 36, "ADD FRIEND", {
        display: true, fontSize: 14,
        onClick: () => {
          if (!this.inView(this.input.activePointer)) return;
          void sendFriendRequest(this.targetUserId!).then(async res => {
            if (!this.scene.isActive()) return;
            if (res.ok) await showAlert(this, "Sent", "Friend request sent!");
            else        await showAlert(this, "Error", res.error);
          });
        }
      });
      const blockBtn = button(this, GAME_WIDTH / 2 + 100, btnY, 180, 36, "BLOCK", {
        display: true, fontSize: 14, color: THEME.colors.blood,
        onClick: () => {
          if (!this.inView(this.input.activePointer)) return;
          void blockUser(this.targetUserId!).then(async res => {
            if (!this.scene.isActive()) return;
            if (res.ok) {
              await showAlert(this, "Blocked", "User has been blocked.");
              goTo(this, "FriendsScene");
            } else {
              await showAlert(this, "Error", res.error);
            }
          });
        }
      });
      this.content.add(addBtn);
      this.content.add(blockBtn);
    } else {
      const btnW = 180;
      const btnSpacing = 220;
      const startX = GAME_WIDTH / 2 - btnSpacing;

      this.content.add(button(this, startX, btnY, btnW, 36, "FRIENDS", {
        display: true, fontSize: 14,
        onClick: () => {
          if (!this.inView(this.input.activePointer)) return;
          goTo(this, "FriendsScene");
        }
      }));
      this.content.add(button(this, startX + btnSpacing, btnY, btnW, 36, "SKILL TREE", {
        display: true, fontSize: 14,
        onClick: () => {
          if (!this.inView(this.input.activePointer)) return;
          goTo(this, "SkillTreeScene");
        }
      }));
      this.content.add(button(this, startX + btnSpacing * 2, btnY, btnW, 36, "SIGN OUT", {
        display: true, fontSize: 14, color: THEME.colors.parchmentDim,
        onClick: () => {
          if (!this.inView(this.input.activePointer)) return;
          void signOut().then(() => goTo(this, "MenuScene"));
        }
      }));
    }

    return 54;
  }

  // ── Scrolling ──────────────────────────────────────────────────────────────
  private applyScroll(delta: number): void {
    this.scrollY = Phaser.Math.Clamp(this.scrollY + delta, 0, this.maxScroll);
    this.content.y = -this.scrollY;
    this.updateScrollBar();
  }

  private rebuildScrollBar(): void {
    this.scrollBar?.destroy();      this.scrollBar = undefined;
    this.scrollBarTrack?.destroy(); this.scrollBarTrack = undefined;
    if (this.maxScroll <= 0) return;

    const barX = GAME_WIDTH - 34;
    this.scrollBarTrack = this.add.rectangle(barX, VIEW_TOP, 6, VIEW_H, THEME.hex.iron, 0.6)
      .setOrigin(0.5, 0).setStrokeStyle(1, THEME.hex.ironEdge, 0.4);

    const thumbH = Math.max(40, VIEW_H * (VIEW_H / (VIEW_H + this.maxScroll)));
    this.scrollBar = this.add.rectangle(barX, VIEW_TOP, 6, thumbH, THEME.hex.goldDim, 0.85)
      .setOrigin(0.5, 0);
    this.updateScrollBar();
  }

  private updateScrollBar(): void {
    if (!this.scrollBar || this.maxScroll <= 0) return;
    const thumbH = this.scrollBar.height;
    const pct = this.scrollY / this.maxScroll;
    this.scrollBar.y = VIEW_TOP + pct * (VIEW_H - thumbH);
  }
}
